'use client';

import { useState, FormEvent, useId, useRef, useEffect } from 'react';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import {
    Elements,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '@/contexts/ToastContext';
import { useTheme } from '@/contexts/ThemeContext';
import { X, Lock, WarningCircle, CheckCircle } from 'phosphor-react';
import { paymentsApi } from '@/api/payments';
import { trackBeginCheckout, trackPurchase } from '@/lib/analytics';
import confetti from 'canvas-confetti';

// Initialize Stripe - Use your publishable key from environment variable
// In Vite, use import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
const getStripeKey = (): string => {
    try {
        return import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY || '';
    } catch {
        return '';
    }
};

const stripePromise = loadStripe(
    getStripeKey() || 'pk_test_51Sthj0K3u06BjvbsuROzf6Ay6a8V5AnU7DV4NXZgpyKGvvN7SbEaknjhy83UNvpqEknyafoBVno63uuq3XFDrT3o00JyVsSz2g'
);

interface StripeCheckoutModalProps {
    isOpen: boolean;
    onClose: () => void;
    amount: number; // Amount in cents (e.g., 8990 for R$ 89,90)
    planName: string;
    planId?: string; // Plan ID for subscription
    onSuccess?: () => void;
}

function CheckoutForm({
    amount,
    planName,
    planId,
    onClose,
    onSuccess,
}: Omit<StripeCheckoutModalProps, 'isOpen'>) {
    const stripe = useStripe();
    const elements = useElements();
    const toast = useToast();
    const { resolvedTheme } = useTheme();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const confettiTriggered = useRef(false);
    
    // Detect if dark mode is active
    const isDark = resolvedTheme === 'dark';

    // Function to trigger confetti
    const triggerConfetti = () => {
        if (confettiTriggered.current) return;
        confettiTriggered.current = true;

        const duration = 3000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

        function randomInRange(min: number, max: number) {
            return Math.random() * (max - min) + min;
        }

        const interval: NodeJS.Timeout = setInterval(() => {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
                clearInterval(interval);
                confettiTriggered.current = false;
                return;
            }

            const particleCount = 50 * (timeLeft / duration);

            // Launch confetti from left
            confetti({
                ...defaults,
                particleCount,
                origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
            });

            // Launch confetti from right
            confetti({
                ...defaults,
                particleCount,
                origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
            });
        }, 250);

        // Final burst
        setTimeout(() => {
            confetti({
                ...defaults,
                particleCount: 100,
                origin: { x: 0.5, y: 0.5 },
                angle: 90,
            });
        }, 500);
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            // Create payment intent on backend with planId if provided
            const { clientSecret, paymentIntentId } = await paymentsApi.createPaymentIntent({
                amount,
                currency: 'brl',
                planId,
            });

            // Get card number element for payment confirmation
            const cardNumberElement = elements.getElement(CardNumberElement);
            if (!cardNumberElement) {
                throw new Error('Card element not found');
            }

            // Confirm payment
            const { error: confirmError, paymentIntent } =
                await stripe.confirmCardPayment(clientSecret, {
                    payment_method: {
                        card: cardNumberElement,
                    },
                });

            if (confirmError) {
                setError(confirmError.message || 'Erro ao processar pagamento');
                toast.error('Erro no pagamento', confirmError.message);
            } else if (paymentIntent?.status === 'succeeded') {
                trackPurchase(planName, amount);
                // Trigger confetti celebration
                triggerConfetti();

                // If planId is provided, this is a subscription - poll for status
                if (planId && paymentIntentId) {
                    toast.success('Pagamento processado! Ativando assinatura...');
                    // Poll for subscription activation (webhook will handle it)
                    // We'll check status in the background
                    setTimeout(async () => {
                        try {
                            const status = await paymentsApi.pollPaymentStatus(
                                paymentIntentId,
                                10,
                                2000,
                            );
                            if (status === 'succeeded') {
                                toast.success('Assinatura ativada com sucesso!');
                                onSuccess?.();
                                // Delay close to show confetti
                                setTimeout(() => {
                                    onClose();
                                }, 2000);
                            } else if (status === 'failed') {
                                toast.error('Erro ao ativar assinatura', 'Tente novamente ou entre em contato com o suporte.');
                            }
                        } catch (pollError) {
                            // Webhook should handle this, but we'll still call onSuccess
                            // as the payment was successful
                            console.warn('Polling error (webhook should handle):', pollError);
                            onSuccess?.();
                            // Delay close to show confetti
                            setTimeout(() => {
                                onClose();
                            }, 2000);
                        }
                    }, 1000);
                } else {
                    // One-time payment
                    toast.success('Pagamento realizado com sucesso!');
                    onSuccess?.();
                    // Delay close to show confetti
                    setTimeout(() => {
                        onClose();
                    }, 2000);
                }
            }
        } catch (err) {
            const message =
                err instanceof Error ? err.message : 'Erro ao processar pagamento';
            setError(message);
            toast.error('Erro no pagamento', message);
        } finally {
            setIsProcessing(false);
        }
    };

    // Determine plan type and features
    const isProPlan = planName.toLowerCase().includes('pro') || planName.toLowerCase().includes('paladino');
    const isCredits = planName.toLowerCase().includes('crédito');
    const isPaymentMethod = planName.toLowerCase().includes('método de pagamento');

    const planFeatures = isProPlan ? [
        'Acesso ilimitado a todos os agentes',
        'Suporte prioritário',
        'Integrações avançadas',
        'Análises e relatórios detalhados',
        'Atualizações em tempo real',
    ] : isCredits ? [
        '1000 créditos adicionais',
        'Sem data de expiração',
        'Uso flexível em qualquer projeto',
    ] : [];

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Plan Summary */}
            {!isPaymentMethod && (
                <div className="p-5 rounded-xl border theme-border bg-gradient-to-br from-[color:var(--nt-primary)]/5 to-[color:var(--nt-primary)]/10">
                    <div className="flex items-start gap-3 mb-4">
                        <div className="h-10 w-10 rounded-xl bg-[color:var(--nt-primary)]/20 flex items-center justify-center shrink-0 overflow-hidden">
                            <img 
                                src="/agent-logo.svg" 
                                alt="Agent Logo" 
                                className="h-full w-full object-contain p-1.5"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-base font-semibold theme-text mb-1">
                                {isProPlan ? 'Paladino Pro' : isCredits ? 'Pacote de Créditos' : planName}
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className="text-2xl font-bold theme-text">
                                    R$ {(amount / 100).toFixed(2).replace('.', ',')}
                                </span>
                                {isProPlan && (
                                    <span className="text-sm theme-text-muted">/mês</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {planFeatures.length > 0 && (
                        <div className="space-y-2 pt-4 border-t theme-border">
                            <div className="text-xs font-semibold theme-text-muted uppercase tracking-wide mb-2">
                                O que está incluído:
                            </div>
                            <div className="space-y-2">
                                {planFeatures.map((feature, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="flex items-center gap-2 text-sm theme-text"
                                    >
                                        <div className="h-5 w-5 rounded-full bg-[color:var(--nt-primary)]/20 flex items-center justify-center shrink-0 overflow-hidden">
                                            <CheckCircle className="size-4 text-[color:var(--nt-primary)]" />
                                        </div>
                                        <span>{feature}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    )}

                    {isProPlan && (
                        <div className="mt-4 pt-4 border-t theme-border">
                            <div className="text-xs theme-text-muted">
                                <span className="font-medium">Renovação automática:</span> Seu plano será renovado automaticamente a cada mês. Você pode cancelar a qualquer momento.
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Payment Summary */}
            <div className="p-4 rounded-xl border theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_50%,transparent)]">
                <div className="mb-3">
                    <div className="text-sm font-semibold theme-text">Resumo do Pagamento</div>
                </div>
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-sm theme-text-muted">Item</span>
                        <span className="text-sm font-medium theme-text">{planName}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t theme-border">
                        <span className="text-base font-semibold theme-text">Total</span>
                        <span className="text-xl font-bold theme-text">
                            R$ {(amount / 100).toFixed(2).replace('.', ',')}
                        </span>
                    </div>
                </div>
            </div>

            {/* Card Input */}
            <div className="space-y-4">
                <div className="mb-3">
                    <label className="block text-sm font-medium theme-text">
                        Informações do Cartão
                    </label>
                </div>

                {/* Card Number */}
                <div className="space-y-1.5">
                    <label className="block text-xs font-medium theme-text-muted">
                        Número do Cartão
                    </label>
                    <div className="p-3.5 rounded-xl border theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_30%,transparent)] transition-all focus-within:border-[color:var(--brand-500)] focus-within:ring-2 focus-within:ring-[color:var(--brand-500)]/40">
                        <div className="StripeElement">
                            <CardNumberElement
                                options={{
                                    style: {
                                        base: {
                                            fontSize: '16px',
                                            color: isDark ? '#ffffff' : '#1a1a1a',
                                            fontFamily: 'system-ui, -apple-system, sans-serif',
                                            '::placeholder': {
                                                color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
                                            },
                                        },
                                        invalid: {
                                            color: '#ef4444',
                                            iconColor: '#ef4444',
                                        },
                                    },
                                    placeholder: '1234 5678 9012 3456',
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Expiry and CVC */}
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <label className="block text-xs font-medium theme-text-muted">
                            Validade
                        </label>
                        <div className="p-3.5 rounded-xl border theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_30%,transparent)] transition-all focus-within:border-[color:var(--brand-500)] focus-within:ring-2 focus-within:ring-[color:var(--brand-500)]/40">
                            <div className="StripeElement">
                                <CardExpiryElement
                                    options={{
                                        style: {
                                            base: {
                                                fontSize: '16px',
                                                color: isDark ? '#ffffff' : '#1a1a1a',
                                                fontFamily: 'system-ui, -apple-system, sans-serif',
                                                '::placeholder': {
                                                    color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
                                                },
                                            },
                                            invalid: {
                                                color: '#ef4444',
                                                iconColor: '#ef4444',
                                            },
                                        },
                                        placeholder: 'MM/AA',
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <label className="block text-xs font-medium theme-text-muted">
                            CVC
                        </label>
                        <div className="p-3.5 rounded-xl border theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_30%,transparent)] transition-all focus-within:border-[color:var(--brand-500)] focus-within:ring-2 focus-within:ring-[color:var(--brand-500)]/40">
                            <div className="StripeElement">
                                <CardCvcElement
                                    options={{
                                        style: {
                                            base: {
                                                fontSize: '16px',
                                                color: isDark ? '#ffffff' : '#1a1a1a',
                                                fontFamily: 'system-ui, -apple-system, sans-serif',
                                                '::placeholder': {
                                                    color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
                                                },
                                            },
                                            invalid: {
                                                color: '#ef4444',
                                                iconColor: '#ef4444',
                                            },
                                        },
                                        placeholder: '123',
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Message */}
            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex items-start gap-2 text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3"
                    >
                        <WarningCircle className="size-5 shrink-0 mt-0.5" />
                        <div className="flex-1">{error}</div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Security Notice */}
            <div className="flex items-start gap-2 text-xs theme-text-muted bg-[color:color-mix(in_oklab,var(--card-bg)_30%,transparent)] rounded-lg px-3 py-2.5">
                <Lock className="size-4 shrink-0 mt-0.5" />
                <div>
                    Seus dados de pagamento são processados de forma segura pela{' '}
                    <span className="font-medium">Stripe</span>. Nós não armazenamos informações do seu cartão.
                </div>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
                <button
                    type="submit"
                    disabled={!stripe || isProcessing}
                    className="w-full inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg text-sm font-semibold transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-60 bg-[color:var(--nt-primary)] text-white fill-white shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom),inset_0_0.125rem_0_0_var(--nt-primary-inset-top)] hover:brightness-95 hover:shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom-hover),inset_0_0.125rem_0_0_var(--nt-primary-inset-top-hover)]"
                >
                    {isProcessing ? (
                        <>
                            <motion.div
                                className="size-4 border-2 border-white/30 border-t-white rounded-full"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            />
                            Processando pagamento...
                        </>
                    ) : (
                        <>
                            <CheckCircle className="size-5 shrink-0 mt-0.5" />
                            Confirmar Pagamento
                        </>
                    )}
                </button>
            </div>
        </form>
    );
}

export default function StripeCheckoutModal({
    isOpen,
    onClose,
    amount,
    planName,
    planId,
    onSuccess,
}: StripeCheckoutModalProps) {
    const titleId = useId();

    useEffect(() => {
        if (isOpen) trackBeginCheckout(planName, amount);
    }, [isOpen, planName, amount]);

    const elementsOptions: StripeElementsOptions = {
        mode: 'payment',
        amount,
        currency: 'brl',
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4"
                    onPointerDown={(e) => {
                        if (e.target === e.currentTarget) onClose();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') onClose();
                    }}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                    tabIndex={-1}
                    style={{ pointerEvents: 'auto' }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.16, ease: 'easeOut' }}
                >
                    <motion.div
                        className="w-full max-w-lg max-h-[80vh] rounded-[28px] border shadow-2xl transition-colors theme-border bg-white dark:bg-[#18181b] overflow-hidden flex flex-col"
                        onPointerDown={(e) => e.stopPropagation()}
                        style={{ pointerEvents: 'auto' }}
                        initial={{ opacity: 0, scale: 0.96, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.98, y: 10 }}
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                    >
                        {/* Header */}
                        <div className="px-6 pt-6 pb-4 border-b theme-border">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-xl bg-[color:var(--nt-primary)]/10 flex items-center justify-center overflow-hidden">
                                        <img 
                                            src="/agent-logo.svg" 
                                            alt="Agent Logo" 
                                            className="h-full w-full object-contain p-1.5"
                                        />
                                    </div>
                                    <div>
                                        <h2 id={titleId} className="text-xl font-semibold theme-text">
                                            Finalizar Pagamento
                                        </h2>
                                        <p className="text-sm theme-text-muted mt-0.5">{planName}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="h-9 w-9 inline-flex items-center justify-center rounded-xl theme-text-muted hover:text-[color:var(--foreground)] hover:bg-[color:color-mix(in_oklab,var(--card-bg)_70%,transparent)] transition-colors"
                                    aria-label="Fechar"
                                >
                                    <X className="size-5" />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 overflow-y-auto flex-1" style={{ pointerEvents: 'auto' }}>
                            <style>{`
                                .StripeElement {
                                    width: 100%;
                                    min-height: 20px;
                                    position: relative;
                                    z-index: 1;
                                }
                                .StripeElement iframe {
                                    pointer-events: auto !important;
                                    position: relative !important;
                                    z-index: 1 !important;
                                }
                                .StripeElement input {
                                    pointer-events: auto !important;
                                }
                                /* Ensure Stripe Elements are clickable */
                                [data-testid="cardNumber-element"],
                                [data-testid="cardExpiry-element"],
                                [data-testid="cardCvc-element"] {
                                    pointer-events: auto !important;
                                }
                            `}</style>
                            <Elements stripe={stripePromise} options={elementsOptions}>
                                <CheckoutForm
                                    amount={amount}
                                    planName={planName}
                                    planId={planId}
                                    onClose={onClose}
                                    onSuccess={onSuccess}
                                />
                            </Elements>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
